import {
  deleteMemberPushSubscription,
  fetchMemberPushConfiguration,
  saveMemberPushSubscription
} from "@/features/public/api";

const PUSH_SERVICE_WORKER_PATH = `${import.meta.env.BASE_URL}asc-notifications-sw.js`;

function urlBase64ToUint8Array(base64String: string) {
  const padding = "=".repeat((4 - (base64String.length % 4 || 4)) % 4);
  const normalizedBase64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(normalizedBase64);
  return Uint8Array.from(rawData, (character) => character.charCodeAt(0));
}

async function ensurePushServiceWorkerRegistration() {
  const registration = await navigator.serviceWorker.register(PUSH_SERVICE_WORKER_PATH);
  await navigator.serviceWorker.ready;
  return registration;
}

export function supportsMemberPushNotifications() {
  return typeof window !== "undefined"
    && "serviceWorker" in navigator
    && "PushManager" in window
    && "Notification" in window;
}

export async function enableMemberPushNotifications(accessToken: string) {
  if (!supportsMemberPushNotifications()) {
    throw new Error("Les notifications push ne sont pas supportées par ce navigateur.");
  }

  let permission = Notification.permission;

  if (permission === "default") {
    permission = await Notification.requestPermission();
  }

  if (permission !== "granted") {
    throw new Error("Vous devez autoriser les notifications pour continuer.");
  }

  const pushConfiguration = await fetchMemberPushConfiguration(accessToken);
  const registration = await ensurePushServiceWorkerRegistration();
  let subscription = await registration.pushManager.getSubscription();

  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(pushConfiguration.publicKey)
    });
  }

  const serializedSubscription = subscription.toJSON();

  if (
    !serializedSubscription.endpoint
    || !serializedSubscription.keys?.p256dh
    || !serializedSubscription.keys?.auth
  ) {
    throw new Error("Impossible d'enregistrer cet appareil pour les notifications push.");
  }

  await saveMemberPushSubscription(accessToken, {
    endpoint: serializedSubscription.endpoint,
    keys: {
      p256dh: serializedSubscription.keys.p256dh,
      auth: serializedSubscription.keys.auth
    }
  });
}

export async function disableMemberPushNotifications(accessToken: string) {
  let endpoint: string | null = null;

  if (supportsMemberPushNotifications()) {
    const registration = await ensurePushServiceWorkerRegistration();
    const subscription = await registration.pushManager.getSubscription();
    endpoint = subscription?.endpoint ?? null;

    if (subscription) {
      await subscription.unsubscribe();
    }
  }

  await deleteMemberPushSubscription(accessToken, endpoint);
}
